import { WidgetProps } from '@highimpact-front/remote-widget-core';
import { GenericErrorBoundary } from '@highimpact/dashboard-ui';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import { PermissionDecorator } from '@highimpact/react-decorators';
import { forwardRef, useMemo } from 'react';
import { useResourcesQuery } from '../../../../../query/useResourcesQuery';
import { useDashboardLibraryDispatch } from '../../../../../runtime/DashboardLibraryProvider';
import {
  setDeleteWidgetConfirmation,
  setWidgetConfigFormIsOpen,
} from '../../../../../state/dashboard/slice';
import NameHandlerComponent from '../../../../NameHandlerComponent';
import Widget from '../../../commons/Widget';
import { WidgetToolbar } from '../../../commons/WidgetToolbar';
import ImageDisplayer, { ApiReturnImageResource } from './ImageDisplayer';
import { useImageInstanceRegistration } from './useImageInstanceRegistration';
import { WidgetDragHandle } from '../../../commons/WidgetDragHandle';

export interface ImageWidgetProps extends WidgetProps {
  project_id: number;
  site_id: number;
  resource_id?: number;
  image: ApiReturnImageResource;
  grid_type: 'reports' | 'grids';
  gridId: number;
}

function ImageWidget(
  {
    project_id,
    site_id,
    image: _image,
    resource_id,
    name,
    dashboardElementId,
    dashboardOnlyMode,
    grid_type,
    gridId,
    non_removable,
    ...props
  }: ImageWidgetProps,
  ref: React.Ref<HTMLDivElement>
): JSX.Element {
  const dispatch = useDashboardLibraryDispatch();
  const hostResourceIds = useMemo(
    () => (resource_id ? [resource_id] : []),
    [resource_id]
  );
  const { resources, loading } = useResourcesQuery(hostResourceIds);
  const loadedImage = resources[0] as ApiReturnImageResource | undefined;

  useImageInstanceRegistration({
    dashboardElementId,
    widgetId: props.widget_id,
    enabled: Boolean(resource_id && !loading),
  });

  const openConfig = () => {
    dispatch(
      setWidgetConfigFormIsOpen({
        dashboardId: gridId,
        widgetId: props.widget_id,
        state: 'open',
        projectId: project_id,
        siteId: site_id,
        grid_type: grid_type,
      })
    );
  };

  return (
    <Widget
      ref={ref}
      {...props}
      className={`image-widget-root ${props.className ?? ''}`.trim()}
      dashboardElementId={dashboardElementId}
    >
      <GenericErrorBoundary>
        <div className="image-widget">
          <PermissionDecorator
            item="dashboard_nav_bar_widget"
            project_id={
              Number.isNaN(project_id) ? undefined : Number(project_id)
            }
            site_id={Number.isNaN(site_id) ? undefined : Number(site_id)}
            extra={{ dashboard_id: gridId }}
          >
            <div className="widget-nav">
              <p style={{ display: 'flex' }}>
                {!dashboardOnlyMode && (
                  <PermissionDecorator item="dashboard_move_widget">
                    <WidgetDragHandle />
                  </PermissionDecorator>
                )}
                <PermissionDecorator item="dashboard_rename_widget">
                  <NameHandlerComponent
                    widgetId={dashboardElementId}
                    dashboardElementId={props.widget_id}
                    dashboardId={gridId}
                    dashboardType={grid_type}
                  />
                </PermissionDecorator>
              </p>
              {!dashboardOnlyMode && (
                <div className="nav-icon">
                  <WidgetToolbar
                    dashboardElementId={dashboardElementId}
                    dashboardId={gridId}
                    // TODO: replace
                    platformActions={{
                      edit: {
                        visible: true,
                        tooltipText: "Configurer l'élément",
                        permissionItem: 'dashboard_configure_widget',
                        onClick: openConfig,
                      },
                      delete: !non_removable
                        ? {
                            visible: true,
                            tooltipText: "Supprimer l'élément",
                            permissionItem: 'dashboard_delete_widget',
                            onClick: () =>
                              dispatch(
                                setDeleteWidgetConfirmation({
                                  state: 'open',
                                  dashboardElementId: dashboardElementId,
                                  widgetName: name ?? 'de nom inconnu',
                                })
                              ),
                          }
                        : undefined,
                    }}
                  />
                </div>
              )}
            </div>
          </PermissionDecorator>
          {resource_id ? (
            loading ? (
              <Box
                className="preview-message"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: 1,
                  flex: '1 1 0',
                  minHeight: 0,
                }}
              >
                <CircularProgress size={20} />
                <Typography variant="body2">
                  Chargement de l&apos;image...
                </Typography>
              </Box>
            ) : (
              <ImageDisplayer image={loadedImage} />
            )
          ) : (
            <Box
              className="preview-message"
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flex: '1 1 0',
                minHeight: 0,
              }}
            >
              <Button variant="contained" onClick={openConfig}>
                Configurer une image
              </Button>
            </Box>
          )}
        </div>
      </GenericErrorBoundary>
    </Widget>
  );
}

export default forwardRef(ImageWidget);
